import z from "zod";
import { ToolCallActionSchema } from "./actions";
import { DesktopContextSchema, DesktopUiEffectSchema } from './desktop-ui';
import { ToolsRegistrySchema } from "./registries";
import { AssistantCapabilitiesSchema } from './assistant-capabilities';
import {
  ErpActionInteractionSchema,
  ErpActionPresenceTargetHintSchema,
  ErpActionTargetSchema,
  ErpInteractionIntentContextSchema,
  ErpInteractionRecentActionSchema,
} from '../erp';

export const ChatRoleSchema = z.enum(['user', 'assistant', 'system', 'tool']);
export type ChatRole = z.infer<typeof ChatRoleSchema>;

export const AssistantRequestContextSchema = z.object({
  origin: z.string().max(100).optional(),
});
export type AssistantRequestContext = z.infer<typeof AssistantRequestContextSchema>;

export const ChatSurfaceIdSchema = z.enum(['desktop', 'admin', 'page']);
export type ChatSurfaceId = z.infer<typeof ChatSurfaceIdSchema>;

export const ChatSurfaceContextSchema = z.object({
  id: ChatSurfaceIdSchema,
  section: z.string().min(1).max(100).optional(),
  referentialKey: z.string().min(1).max(160).optional(),
});
export type ChatSurfaceContext = z.infer<typeof ChatSurfaceContextSchema>;

export const ChatNavigationContextSchema = z.object({
  route: z.string().max(500).optional(),
  currentTargets: z.array(ErpActionTargetSchema).max(32).optional(),
}).catchall(z.unknown());
export type ChatNavigationContext = z.infer<typeof ChatNavigationContextSchema>;

export const ChatAppContextSchema = z.object({
  surface: ChatSurfaceContextSchema.optional(),
  navigation: ChatNavigationContextSchema.optional(),
  desktop: DesktopContextSchema.optional(),
}).catchall(z.record(z.string(), z.unknown()));
export type ChatAppContext = z.infer<typeof ChatAppContextSchema>;

const AssistantPromptSourceSchema = z.object({
  name: z.string().min(1).max(255),
  mimeType: z.string().min(1).max(255),
  size: z.number().int().nonnegative().max(1_000_000_000).optional(),
  semanticType: z.string().min(1).max(100).optional(),
}).strict();

/**
 * Server-owned, bounded context contract sent to the ERP assistant prompt.
 * It is intentionally separate from the client-provided appContext hints.
 */
export const AssistantPromptContextSchema = z.object({
  interaction: z.object({
    intent: ErpInteractionIntentContextSchema.optional(),
    actions: z.object({
      recent: z.array(ErpInteractionRecentActionSchema).max(10),
    }).strict().optional(),
    sources: z.object({
      current: z.array(AssistantPromptSourceSchema).max(32),
    }).strict().optional(),
  }).strict().optional(),
  surface: ChatSurfaceContextSchema.optional(),
  navigation: z.object({
    route: z.string().max(500).optional(),
    currentTargets: z.array(ErpActionTargetSchema).max(32).optional(),
  }).strict().optional(),
  desktop: DesktopContextSchema.optional(),
  individual: z.object({
    memory: z.object({
      summary: z.string().max(4_000),
    }).strict().optional(),
  }).strict().optional(),
  business: z.object({
    instructions: z.object({
      workspace: z.string().max(4_000),
    }).strict().optional(),
    entities: z.object({
      current: z.array(z.object({
        type: z.enum(['resource', 'company', 'contact', 'need', 'positioning']),
        id: z.string().min(1).max(200),
        label: z.string().max(200).optional(),
        status: z.string().max(100).optional(),
      }).strict()).max(3),
    }).strict().optional(),
  }).strict().optional(),
  knowledge: z.object({
    productDocumentation: z.array(z.object({
      title: z.string().min(1).max(200),
      content: z.string().min(1).max(4_000),
      sourceUrl: z.string().max(2_000).optional(),
      excerpt: z.string().max(500).optional(),
    }).strict()).max(3),
  }).strict().optional(),
}).strict();
export type AssistantPromptContext = z.infer<typeof AssistantPromptContextSchema>;

export const ChatTurnDiagnosticContextTruncationDetailSchema = z.object({
  path: z.string().max(200),
  kind: z.enum(['depth', 'string', 'array', 'messages', 'snapshot_size', 'invalid']),
  originalSize: z.number().int().nonnegative().optional(),
  keptSize: z.number().int().nonnegative().optional(),
});
export type ChatTurnDiagnosticContextTruncationDetail = z.infer<typeof ChatTurnDiagnosticContextTruncationDetailSchema>;

export const ChatTurnDiagnosticContextSanitizationSchema = z.object({
  truncated: z.literal(true),
  details: z.array(ChatTurnDiagnosticContextTruncationDetailSchema).min(1).max(100),
});
export type ChatTurnDiagnosticContextSanitization = z.infer<typeof ChatTurnDiagnosticContextSanitizationSchema>;

export const ChatTurnDiagnosticContextSchema = z.object({
  requestContext: AssistantRequestContextSchema.optional(),
  appContext: ChatAppContextSchema.optional(),
  sanitization: ChatTurnDiagnosticContextSanitizationSchema.optional(),
});
export type ChatTurnDiagnosticContext = z.infer<typeof ChatTurnDiagnosticContextSchema>;

export const ChatAttachmentSchema = z.object({
  name: z.string(),
  type: z.string(),
  data: z.string(),
  semanticType: z.string().trim().min(1).max(100).optional(),
});
export type ChatAttachment = z.infer<typeof ChatAttachmentSchema>;

/** Per-message limit for files submitted to the Assistant. */
export const CHAT_ASSISTANT_LIMITS = {
  maxAttachmentsPerMessage: 5,
} as const;

/**
 * Stable identity shared by ERP source discovery and chatbot attachment
 * resolution. Position keeps repeated files distinct.
 */
export function buildChatAttachmentSourceId(
  conversationId: string | undefined,
  messageIndex: number,
  attachmentIndex: number,
  attachment: Pick<ChatAttachment, 'name' | 'type' | 'data'>,
): string {
  const payload = [
    conversationId ?? 'current-turn',
    messageIndex,
    attachmentIndex,
    attachment.name,
    attachment.type,
    attachment.data,
  ].join('\u0000');
  let hash = 14695981039346656037n;
  for (const character of payload) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * 1099511628211n);
  }
  return `attachment-${hash.toString(16).padStart(16, '0')}`;
}

export const ChatAttachmentReferenceSchema = z.object({
  sourceId: z.string().trim().min(1).max(256).optional(),
  storageId: z.string().trim().min(1).max(128).optional(),
  name: z.string().min(1).max(255),
  type: z.string().min(1).max(255),
  fingerprint: z.string().min(1).max(256),
  semanticType: z.string().trim().min(1).max(100).optional(),
});
export type ChatAttachmentReference = z.infer<typeof ChatAttachmentReferenceSchema>;

export const ChatContinuationAttachmentInputSchema = z.object({
  sourceId: z.string().trim().min(1).max(256),
  name: z.string().min(1).max(255),
  type: z.string().min(1).max(255),
  data: z.string(),
  semanticType: z.string().trim().min(1).max(100).optional(),
}).strict();
export type ChatContinuationAttachmentInput = z.infer<typeof ChatContinuationAttachmentInputSchema>;

export const ChatContinuationAttachmentResolveRequestSchema = z.object({
  conversationId: z.string().trim().min(1).max(256),
  pendingInputId: z.string().trim().min(1).max(256),
  actionId: z.string().trim().min(1).max(160),
  sourceId: z.string().trim().min(1).max(256),
}).strict();
export type ChatContinuationAttachmentResolveRequest = z.infer<typeof ChatContinuationAttachmentResolveRequestSchema>;

export const ChatContinuationAttachmentReferenceSchema = ChatAttachmentReferenceSchema.extend({
  storageId: z.string().trim().min(1).max(128),
  sourceId: z.string().trim().min(1).max(256),
}).strict();
export type ChatContinuationAttachmentReference = z.infer<typeof ChatContinuationAttachmentReferenceSchema>;

export const ChatContinuationAttachmentResolveResponseSchema = z.object({
  attachment: ChatAttachmentSchema,
}).strict();
export type ChatContinuationAttachmentResolveResponse = z.infer<typeof ChatContinuationAttachmentResolveResponseSchema>;

const ChatPendingAttachmentMatchSchema = z.object({
  slot: z.string().trim().min(1).max(80),
  cardinality: z.enum(['one', 'many']),
  sourceIds: z.array(z.string().trim().min(1).max(256)).max(32),
}).strict();

/** Limits for the transient Assistant continuation state, not conversation history. */
export const ASSISTANT_CONTINUATION_LIMITS = {
  pendingInputTtlMs: 30 * 60 * 1000,
  maxInputRounds: 5,
  maxArgsStringLength: 32 * 1024,
  maxPendingInputActions: 10,
  maxPendingPreparedActions: 10,
  maxAttachmentsPerPendingInput: 16,
  maxRecentActions: 10,
} as const;

export function assistantContinuationArgsByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

export const ChatContinuationAttachmentPutRequestSchema = z.object({
  conversationId: z.string().trim().min(1).max(256),
  pendingInputId: z.string().trim().min(1).max(256),
  actionId: z.string().trim().min(1).max(160),
  attachments: z.array(ChatContinuationAttachmentInputSchema).max(ASSISTANT_CONTINUATION_LIMITS.maxAttachmentsPerPendingInput),
}).strict();
export type ChatContinuationAttachmentPutRequest = z.infer<typeof ChatContinuationAttachmentPutRequestSchema>;

export const ChatContinuationAttachmentPutResponseSchema = z.object({
  references: z.array(ChatContinuationAttachmentReferenceSchema).max(ASSISTANT_CONTINUATION_LIMITS.maxAttachmentsPerPendingInput),
}).strict();
export type ChatContinuationAttachmentPutResponse = z.infer<typeof ChatContinuationAttachmentPutResponseSchema>;

export const ChatContinuationScopeSchema = z.object({
  identityId: z.string().min(1),
  membershipId: z.string().min(1),
  workspaceId: z.string().min(1),
  sessionId: z.string().min(1),
}).strict();
export type ChatContinuationScope = z.infer<typeof ChatContinuationScopeSchema>;

const ChatPendingPreparedActionSchema = z.object({
    callId: z.string(),
    actionId: z.string(),
    preparedId: z.string(),
    confirmationId: z.string().optional(),
    expiresAt: z.string(), // ISO date string
    resolved: z.unknown().optional(),
    interaction: ErpActionInteractionSchema.optional(),
}).strict();

const ChatPendingInputActionFieldsSchema = z.object({
    kind: z.enum(['requires-input', 'requires-disambiguation']).optional(),
    pendingInputId: z.string(),
    callId: z.string(),
    actionId: z.string(),
    argsString: z.string().refine(
        (value) => assistantContinuationArgsByteLength(value) <= ASSISTANT_CONTINUATION_LIMITS.maxArgsStringLength,
        `argsString must be at most ${ASSISTANT_CONTINUATION_LIMITS.maxArgsStringLength} bytes`,
    ),
    attachments: z.array(ChatAttachmentReferenceSchema).max(ASSISTANT_CONTINUATION_LIMITS.maxAttachmentsPerPendingInput),
    attachmentMatches: z.array(ChatPendingAttachmentMatchSchema).max(32).optional(),
    reason: z.string().max(120).optional(),
    requirements: z.array(z.record(z.string(), z.unknown())).max(32).optional(),
    intent: ErpInteractionIntentContextSchema.optional(),
}).strict();

const ChatPendingInputActionSchema = ChatPendingInputActionFieldsSchema.extend({
    // Optional keeps old stateless clients readable; ERP persistence only forwards fresh bounded state.
    round: z.number().int().min(1).max(ASSISTANT_CONTINUATION_LIMITS.maxInputRounds).optional(),
    expiresAt: z.string().optional(),
}).strict();

export const ChatPersistedPendingInputActionSchema = ChatPendingInputActionFieldsSchema.extend({
    round: z.number().int().min(1).max(ASSISTANT_CONTINUATION_LIMITS.maxInputRounds),
    expiresAt: z.string(),
}).strict();
export type ChatPersistedPendingInputAction = z.infer<typeof ChatPersistedPendingInputActionSchema>;

export const ChatEntityResultSchema = z.object({
  type: z.enum(['company', 'contact', 'opportunity', 'resource']),
  label: z.string(),
  href: z.string(),
  subtitle: z.string().optional(),
  imageUrl: z.string().url().optional(),
  fields: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
});
export type ChatEntityResult = z.infer<typeof ChatEntityResultSchema>;

export const ChatEntityPresentationSchema = z.object({
  kind: z.literal('entity-results'),
  results: z.array(ChatEntityResultSchema),
  hasMore: z.boolean().optional(),
});
export type ChatEntityPresentation = z.infer<typeof ChatEntityPresentationSchema>;

export const ChatMessageSchema = z.object({
  role: ChatRoleSchema,
  content: z.string(),
  toolCalls: z.array(ToolCallActionSchema).nullish(),
  name: z.string().optional(),
  toolCallId: z.string().optional(),
  attachments: z.array(ChatAttachmentSchema).max(CHAT_ASSISTANT_LIMITS.maxAttachmentsPerMessage).optional(),
  presentation: ChatEntityPresentationSchema.optional(),
  generationContext: ChatTurnDiagnosticContextSchema.optional(),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export const ChatCitationSchema = z.object({
    title: z.string(),
    sourceUrl: z.string(),
    excerpt: z.string(),
});
export type ChatCitation = z.infer<typeof ChatCitationSchema>;

export const ChatStateSchema = z.object({
    blockedActions: z.array(z.object({
        callId: z.string(),
        capabilityId: z.string(),
        argsString: z.string(),
        dependencies: z.array(z.string()),
    })).optional(),
    actionResults: z.record(z.string(), z.any()).optional(),
    recentActions: z.array(ErpInteractionRecentActionSchema).max(10).optional(),
    pendingPreparedActions: z.array(ChatPendingPreparedActionSchema).max(ASSISTANT_CONTINUATION_LIMITS.maxPendingPreparedActions).optional(),
    pendingInputActions: z.array(ChatPendingInputActionSchema).max(ASSISTANT_CONTINUATION_LIMITS.maxPendingInputActions).optional(),
}).strict();
export type ChatState = z.infer<typeof ChatStateSchema>;

export const ChatUsageSchema = z.object({
    promptTokens: z.number(),
    completionTokens: z.number(),
    toolsCalled: z.array(z.object({
        name: z.string(),
        label: z.string(),
    })).optional(),
});
export type ChatUsage = z.infer<typeof ChatUsageSchema>;

export const ChatResponseSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('tool_calls'),
        assistantMessage: ChatMessageSchema,
        actions: z.array(ToolCallActionSchema),
        stateUpdate: ChatStateSchema.optional(),
        usage: ChatUsageSchema.optional(),
    }),
    z.object({
        type: z.literal('text'),
        assistantMessage: ChatMessageSchema,
        content: z.string(),
        citations: z.array(ChatCitationSchema),
        stateUpdate: ChatStateSchema.optional(),
        usage: ChatUsageSchema.optional(),
    }),
    z.object({
        type: z.literal('error'),
        message: z.string(),
        stateUpdate: ChatStateSchema.optional(),
        usage: ChatUsageSchema.optional(),
    }),
]);
export type ChatResponse = z.infer<typeof ChatResponseSchema>;

export const ChatStreamChunkSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('content'),
    content: z.string(),
  }),
  z.object({
    type: z.literal('tool_calls'),
    assistantMessage: ChatMessageSchema,
    actions: z.array(ToolCallActionSchema),
  }),
  z.object({
    type: z.literal('text'),
    assistantMessage: ChatMessageSchema,
    content: z.string(),
    citations: z.array(ChatCitationSchema),
  }),
  z.object({
    type: z.literal('error'),
    message: z.string(),
  }),
  z.object({
    type: z.literal('tool_result'),
    toolCallId: z.string(),
    name: z.string(),
    result: z.string(),
  }),
  z.object({
    type: z.literal('state_update'),
    state: ChatStateSchema,
  }),
  z.object({
    type: z.literal('usage_update'),
    usage: ChatUsageSchema,
  }),
  z.object({
    type: z.literal('desktop_ui_effects'),
    effects: z.array(DesktopUiEffectSchema).min(1),
  }),
  z.object({
    type: z.literal('assistant_presence'),
    phase: z.enum(['start', 'update', 'end']),
    operationId: z.string().trim().min(1).max(160),
    actionId: z.string().trim().min(1).max(160),
    activity: z.enum(['editing', 'executing']).optional(),
    focusHint: ErpActionPresenceTargetHintSchema.optional(),
    hoverHint: z.object({ kind: z.literal('control'), controlId: z.string().trim().min(1).max(160) }).strict().optional(),
    choreography: z.array(z.object({
      activity: z.literal('editing'),
      focusHint: z.object({ kind: z.literal('field'), controlId: z.string().trim().min(1).max(160) }).strict(),
    }).strict()).max(5).optional(),
    targets: z.array(z.object({
      type: z.string().trim().min(1).max(80),
      id: z.string().trim().min(1).max(200),
    }).strict()).max(16).optional(),
  }).strict(),
]);
export type ChatStreamChunk = z.infer<typeof ChatStreamChunkSchema>;
export type AssistantPresenceStreamChunk = Extract<ChatStreamChunk, { type: 'assistant_presence' }>;
export type ToolCallChunk = Extract<ChatStreamChunk, { type: 'tool_calls' }>;

export const ChatRequestSchema = z.object({
  conversationId: z.string().optional(),
  messages: z.array(ChatMessageSchema),
  requestContext: AssistantRequestContextSchema.optional(),
  appContext: ChatAppContextSchema.optional().default({}),
  tools: ToolsRegistrySchema.optional().default({}),
  state: ChatStateSchema.optional(),
});
export type ChatRequest = z.infer<typeof ChatRequestSchema>;

export const InternalChatRequestSchema = ChatRequestSchema.extend({
  assistantContext: AssistantPromptContextSchema.optional(),
  assistantCapabilities: AssistantCapabilitiesSchema.optional(),
});
export type InternalChatRequest = z.infer<typeof InternalChatRequestSchema>;

export const ChatTitleRequestSchema = z.object({
  message: z.string(),
  assistantResponse: z.string().optional(),
});
export type ChatTitleRequest = z.infer<typeof ChatTitleRequestSchema>;

export const ChatTitleResponseSchema = z.object({
  title: z.string(),
});
export type ChatTitleResponse = z.infer<typeof ChatTitleResponseSchema>;

export const ChatConversationSchema = z.object({
    id: z.string(),
    title: z.string(),
    messages: z.array(ChatMessageSchema),
    updatedAt: z.number(),
    pendingPreparedActions: z.array(ChatPendingPreparedActionSchema).max(ASSISTANT_CONTINUATION_LIMITS.maxPendingPreparedActions).optional(),
    pendingInputActions: z.array(ChatPersistedPendingInputActionSchema).max(ASSISTANT_CONTINUATION_LIMITS.maxPendingInputActions).optional(),
    blockedActions: z.array(z.object({
        callId: z.string(),
        capabilityId: z.string(),
        argsString: z.string(),
        dependencies: z.array(z.string()),
    })).optional(),
    actionResults: z.record(z.string(), z.any()).optional(),
    recentActions: z.array(ErpInteractionRecentActionSchema).max(10).optional(),
});
export type ChatConversation = z.infer<typeof ChatConversationSchema>;

export const CreateChatConversationSchema = z.object({
    id: z.string(),
    title: z.string(),
    messages: z.array(ChatMessageSchema),
});
export type CreateChatConversation = z.infer<typeof CreateChatConversationSchema>;

export const UpdateChatConversationSchema = z.object({
    title: z.string().optional(),
    messages: z.array(ChatMessageSchema).optional(),
    pendingPreparedActions: z.array(ChatPendingPreparedActionSchema).max(ASSISTANT_CONTINUATION_LIMITS.maxPendingPreparedActions).optional().nullable(),
    pendingInputActions: z.array(ChatPersistedPendingInputActionSchema).max(ASSISTANT_CONTINUATION_LIMITS.maxPendingInputActions).optional().nullable(),
    blockedActions: z.array(z.object({
        callId: z.string(),
        capabilityId: z.string(),
        argsString: z.string(),
        dependencies: z.array(z.string()),
    })).optional().nullable(),
    actionResults: z.record(z.string(), z.any()).optional().nullable(),
    recentActions: z.array(ErpInteractionRecentActionSchema).max(10).optional().nullable(),
});
export type UpdateChatConversation = z.infer<typeof UpdateChatConversationSchema>;
